import { createContext, useContext, useState, useCallback } from "react";

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

const newLineId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      // Merge simple items (no customization) by name
      if (item.type === "simple") {
        const idx = prev.findIndex(
          (p) => p.type === "simple" && p.name === item.name,
        );
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], qty: copy[idx].qty + item.qty };
          return copy;
        }
      }
      return [...prev, { ...item, lineId: newLineId() }];
    });
  }, []);

  const incQty = useCallback((lineId) => {
    setItems((prev) =>
      prev.map((p) => (p.lineId === lineId ? { ...p, qty: p.qty + 1 } : p)),
    );
  }, []);

  const decQty = useCallback((lineId) => {
    setItems((prev) =>
      prev
        .map((p) => (p.lineId === lineId ? { ...p, qty: p.qty - 1 } : p))
        .filter((p) => p.qty > 0),
    );
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems((prev) => prev.filter((p) => p.lineId !== lineId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setComments("");
  }, []);

  const totalCount = items.reduce((sum, i) => sum + i.qty, 0);
  const totalAmount = items.reduce((sum, i) => sum + i.unitPrice * i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        incQty,
        decQty,
        removeItem,
        clearCart,
        comments,
        setComments,
        isOpen,
        openCart,
        closeCart,
        totalCount,
        totalAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
